import { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { haptic } from "../utils/haptic";

// ============================================================================
// XTRIP VIBE ENGINE - Hernan Cattaneo Edition
// Web Audio API High-Precision Looping System
// With CORS-bypass proxy, dynamic URL refresh, and expired link recovery
// ============================================================================

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audio settings - Progressive House tempo
const BPM = 122;                          // Typical Hernan Cattaneo tempo
const FADE_DURATION = 1.0;                // Longer crossfade for smooth transitions
const MASTER_VOLUME = 0.4;                // Master output volume (0-1)
const TRACK_CACHE_TTL = 1800000;          // Cache tracks for 30 min (Deezer URLs expire)

// Calculate beat interval for visual pulse (ms)
const BEAT_INTERVAL = (60 / BPM) * 1000;  // ~492ms at 122 BPM

// Local storage key for cached tracks
const TRACKS_CACHE_KEY = 'xtrip_vibe_tracks';
const TRACKS_CACHE_TIME_KEY = 'xtrip_vibe_tracks_time';

// Maximum retry attempts for failed audio loads
const MAX_RETRY_ATTEMPTS = 3;

// ============================================================================

const AudioController = ({ showMuteInFooter = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  
  // Web Audio API refs
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const pulseIntervalRef = useRef(null);

  // Force refresh tracks from API (clears cache)
  const refreshTracks = useCallback(async () => {
    console.log("[Vibe Engine] Force refreshing tracks from API...");
    
    // Clear cache
    try {
      localStorage.removeItem(TRACKS_CACHE_KEY);
      localStorage.removeItem(TRACKS_CACHE_TIME_KEY);
    } catch (e) {
      // Ignore storage errors
    }
    
    // Fetch fresh tracks
    try {
      const response = await fetch(`${API_URL}/api/vibe/tracks?fresh=1`);
      if (response.ok) {
        const data = await response.json();
        if (data.tracks && data.tracks.length > 0) {
          setTracks(data.tracks);
          
          // Cache the tracks
          try {
            localStorage.setItem(TRACKS_CACHE_KEY, JSON.stringify(data.tracks));
            localStorage.setItem(TRACKS_CACHE_TIME_KEY, Date.now().toString());
          } catch (e) {
            // Ignore storage errors
          }
          
          console.log(`[Vibe Engine] Refreshed ${data.tracks.length} tracks`);
          return data.tracks;
        }
      }
    } catch (error) {
      console.log("[Vibe Engine] Refresh failed:", error.message);
    }
    
    return [];
  }, []);

  // Fetch tracks from API on mount (with caching)
  useEffect(() => {
    const fetchTracks = async () => {
      // Check cache first
      try {
        const cachedTracks = localStorage.getItem(TRACKS_CACHE_KEY);
        const cacheTime = localStorage.getItem(TRACKS_CACHE_TIME_KEY);
        
        if (cachedTracks && cacheTime) {
          const age = Date.now() - parseInt(cacheTime, 10);
          if (age < TRACK_CACHE_TTL) {
            const parsed = JSON.parse(cachedTracks);
            if (parsed.length > 0) {
              setTracks(parsed);
              console.log(`[Vibe Engine] Loaded ${parsed.length} tracks from cache (age: ${Math.round(age/1000)}s)`);
              return;
            }
          }
        }
      } catch (e) {
        console.log('[Vibe Engine] Cache read error:', e.message);
      }
      
      // Fetch fresh tracks from API
      try {
        const response = await fetch(`${API_URL}/api/vibe/tracks`);
        if (response.ok) {
          const data = await response.json();
          if (data.tracks && data.tracks.length > 0) {
            setTracks(data.tracks);
            
            // Cache the tracks
            try {
              localStorage.setItem(TRACKS_CACHE_KEY, JSON.stringify(data.tracks));
              localStorage.setItem(TRACKS_CACHE_TIME_KEY, Date.now().toString());
            } catch (e) {
              console.log('[Vibe Engine] Cache write error:', e.message);
            }
            
            console.log(`[Vibe Engine] Fetched ${data.tracks.length} fresh tracks from API`);
          }
        }
      } catch (error) {
        console.log("[Vibe Engine] Could not fetch tracks from API:", error.message);
      }
    };
    
    fetchTracks();
  }, []);

  // Report play event to backend for monitoring
  const reportPlayEvent = useCallback(async (eventType, trackTitle, error = null, duration = 0) => {
    try {
      await fetch(`${API_URL}/api/vibe/play-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: eventType,
          track_title: trackTitle,
          error: error,
          duration: duration
        })
      });
    } catch (e) {
      // Silent fail - don't break audio for analytics
    }
  }, []);

  // Get random track from fetched list
  const getRandomTrack = useCallback(() => {
    if (tracks.length === 0) return null;
    const idx = Math.floor(Math.random() * tracks.length);
    return tracks[idx];
  }, [tracks]);

  // Fetch audio with CORS proxy fallback
  const fetchAudioWithFallback = useCallback(async (url) => {
    // First try direct fetch (works for most Deezer URLs)
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        return await response.arrayBuffer();
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (directError) {
      console.log("[Vibe Engine] Direct fetch failed, trying proxy...", directError.message);
      
      // Fall back to our backend proxy
      try {
        const proxyUrl = `${API_URL}/api/vibe/proxy?url=${encodeURIComponent(url)}`;
        const proxyResponse = await fetch(proxyUrl);
        if (proxyResponse.ok) {
          console.log("[Vibe Engine] Proxy fetch succeeded");
          return await proxyResponse.arrayBuffer();
        }
        throw new Error(`Proxy HTTP ${proxyResponse.status}`);
      } catch (proxyError) {
        console.log("[Vibe Engine] Proxy fetch also failed:", proxyError.message);
        throw proxyError;
      }
    }
  }, []);

  // Initialize Web Audio API and load track
  const initAudio = useCallback(async (track, attemptFresh = false) => {
    if (!track) {
      console.log("[Vibe Engine] No track available");
      setIsLoaded(true);
      return false;
    }

    try {
      setIsLoading(true);
      
      // Create AudioContext (suspended until user interaction)
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
        
        // Create master gain node for volume control
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = 0; // Start silent
        gainNodeRef.current.connect(audioContextRef.current.destination);
      }
      
      setCurrentTrack(track);
      
      // Fetch and decode audio file with CORS fallback
      console.log(`[Vibe Engine] Loading: ${track.title}`);
      const arrayBuffer = await fetchAudioWithFallback(track.url);
      audioBufferRef.current = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      setIsLoaded(true);
      setIsLoading(false);
      setRetryCount(0); // Reset retry count on success
      console.log("[Vibe Engine] Track loaded successfully");
      console.log(`[Vibe Engine] Track: ${track.title}`);
      console.log(`[Vibe Engine] Duration: ${audioBufferRef.current.duration.toFixed(2)}s`);
      
      return true;
      
    } catch (error) {
      console.error("[Vibe Engine] Failed to load track:", error);
      setIsLoading(false);
      
      // Report error for monitoring
      reportPlayEvent('error', track.title, error.message);
      
      // Check if this is a CORS or expired URL error
      const isExpiredOrCors = error.message.includes('403') || 
                              error.message.includes('CORS') ||
                              error.message.includes('Failed to fetch') ||
                              error.message.includes('NetworkError');
      
      // If expired/CORS error and haven't refreshed yet, try getting fresh URLs
      if (isExpiredOrCors && !attemptFresh && retryCount < MAX_RETRY_ATTEMPTS) {
        console.log("[Vibe Engine] URL may be expired, refreshing tracks...");
        setRetryCount(prev => prev + 1);
        
        const freshTracks = await refreshTracks();
        if (freshTracks.length > 0) {
          const freshTrack = freshTracks[Math.floor(Math.random() * freshTracks.length)];
          return await initAudio(freshTrack, true);
        }
      }
      
      // Try another track if available
      if (tracks.length > 1 && retryCount < MAX_RETRY_ATTEMPTS) {
        const otherTracks = tracks.filter(t => t.url !== track.url);
        if (otherTracks.length > 0) {
          console.log("[Vibe Engine] Trying alternate track...");
          setRetryCount(prev => prev + 1);
          const altTrack = otherTracks[Math.floor(Math.random() * otherTracks.length)];
          return await initAudio(altTrack, attemptFresh);
        }
      }
      
      setIsLoaded(true); // Allow UI interaction even if audio fails
      return false;
    }
  }, [tracks, fetchAudioWithFallback, refreshTracks, reportPlayEvent, retryCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.stop();
          sourceNodeRef.current.disconnect();
        } catch (e) { /* ignore */ }
      }
      if (pulseIntervalRef.current) {
        clearInterval(pulseIntervalRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Create and start a new source node with looping
  const createSourceNode = useCallback(() => {
    if (!audioContextRef.current || !audioBufferRef.current) return null;
    
    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBufferRef.current;
    source.connect(gainNodeRef.current);
    
    // Loop the entire 30-second Deezer preview
    source.loop = true;
    source.loopStart = 0;
    source.loopEnd = audioBufferRef.current.duration;
    
    return source;
  }, []);

  // Start playback with crossfade
  const startPlayback = useCallback(async () => {
    if (!audioContextRef.current || !audioBufferRef.current) return;
    
    // Resume context if suspended (browser autoplay policy)
    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }
    
    // Stop any existing playback
    if (sourceNodeRef.current) {
      sourceNodeRef.current.stop();
      sourceNodeRef.current.disconnect();
    }
    
    // Create new source node
    sourceNodeRef.current = createSourceNode();
    if (!sourceNodeRef.current) return;
    
    // Start from beginning
    const now = audioContextRef.current.currentTime;
    sourceNodeRef.current.start(0);
    
    // Crossfade in (smooth fade to prevent pop)
    gainNodeRef.current.gain.cancelScheduledValues(now);
    gainNodeRef.current.gain.setValueAtTime(0, now);
    gainNodeRef.current.gain.linearRampToValueAtTime(
      isMuted ? 0 : MASTER_VOLUME, 
      now + FADE_DURATION
    );
    
    // Report play event for monitoring
    if (currentTrack) {
      reportPlayEvent('play', currentTrack.title);
    }
    
    // Start visual pulse synced to BPM
    if (pulseIntervalRef.current) clearInterval(pulseIntervalRef.current);
    setIsPulsing(true);
    pulseIntervalRef.current = setInterval(() => {
      setIsPulsing(prev => !prev);
    }, BEAT_INTERVAL / 2);
    
    console.log("[Vibe Engine] Playing with crossfade");
  }, [createSourceNode, isMuted]);

  // Stop playback with crossfade
  const stopPlayback = useCallback(() => {
    if (!audioContextRef.current || !sourceNodeRef.current) return;
    
    const now = audioContextRef.current.currentTime;
    
    // Crossfade out
    gainNodeRef.current.gain.cancelScheduledValues(now);
    gainNodeRef.current.gain.setValueAtTime(gainNodeRef.current.gain.value, now);
    gainNodeRef.current.gain.linearRampToValueAtTime(0, now + FADE_DURATION);
    
    // Schedule stop after fade completes
    setTimeout(() => {
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.stop();
          sourceNodeRef.current.disconnect();
        } catch (e) {
          // Source may already be stopped
        }
        sourceNodeRef.current = null;
      }
    }, FADE_DURATION * 1000 + 50);
    
    // Stop visual pulse
    if (pulseIntervalRef.current) {
      clearInterval(pulseIntervalRef.current);
      pulseIntervalRef.current = null;
    }
    setIsPulsing(false);
    
    console.log("[Vibe Engine] Playback stopped with crossfade");
  }, []);

  // Toggle play/pause
  const togglePlay = useCallback(async () => {
    // Haptic feedback on vibe toggle
    haptic("light");
    
    if (isPlaying) {
      stopPlayback();
      setIsPlaying(false);
    } else {
      // If not loaded yet, load first
      if (!audioBufferRef.current && tracks.length > 0) {
        const track = getRandomTrack();
        const success = await initAudio(track);
        
        if (success && audioBufferRef.current) {
          await startPlayback();
          setIsPlaying(true);
        }
      } else if (audioBufferRef.current) {
        await startPlayback();
        setIsPlaying(true);
      } else if (tracks.length === 0) {
        // No tracks - try refreshing
        console.log("[Vibe Engine] No tracks available, refreshing...");
        const freshTracks = await refreshTracks();
        if (freshTracks.length > 0) {
          const track = freshTracks[Math.floor(Math.random() * freshTracks.length)];
          const success = await initAudio(track);
          if (success && audioBufferRef.current) {
            await startPlayback();
            setIsPlaying(true);
          }
        } else {
          console.log("[Vibe Engine] No audio available to play");
        }
      }
    }
  }, [isPlaying, tracks, getRandomTrack, initAudio, startPlayback, stopPlayback, refreshTracks]);

  // Toggle mute (adjusts gain node, doesn't stop playback)
  const toggleMute = useCallback(() => {
    if (!gainNodeRef.current || !audioContextRef.current) return;
    
    const now = audioContextRef.current.currentTime;
    const newMuted = !isMuted;
    
    gainNodeRef.current.gain.cancelScheduledValues(now);
    gainNodeRef.current.gain.setValueAtTime(gainNodeRef.current.gain.value, now);
    gainNodeRef.current.gain.linearRampToValueAtTime(
      newMuted ? 0 : MASTER_VOLUME,
      now + 0.1 // Quick fade for mute toggle
    );
    
    setIsMuted(newMuted);
  }, [isMuted]);

  // Footer mute button
  if (showMuteInFooter) {
    if (!isPlaying) return null;
    return (
      <button
        onClick={toggleMute}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        data-testid="audio-mute-footer"
      >
        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        {isMuted ? "Unmute" : "Mute"}
      </button>
    );
  }

  // Check if we have tracks available
  const hasTracksAvailable = tracks.length > 0;

  // Header play button with BPM-synced pulse
  return (
    <button
      onClick={togglePlay}
      disabled={isLoading || !hasTracksAvailable}
      className={`
        relative flex items-center gap-2 px-3 py-1.5 text-xs font-medium uppercase tracking-wider 
        transition-all duration-100 rounded-sm
        ${(isLoading || !hasTracksAvailable) ? "opacity-50 cursor-wait" : "cursor-pointer"}
        ${isPlaying 
          ? `text-primary border border-primary ${isPulsing ? "bg-primary/20 shadow-[0_0_15px_rgba(255,0,0,0.5)]" : "bg-primary/10"}` 
          : "text-muted-foreground border border-border hover:border-primary hover:text-primary hover:animate-[vibe-pulse_492ms_ease-in-out_infinite]"
        }
      `}
      data-testid="audio-toggle"
      title={currentTrack ? `Now Playing: ${currentTrack.title}` : "Play Vibe"}
    >
      {isPlaying ? (
        <>
          <Volume2 className={`w-3.5 h-3.5 transition-transform duration-100 ${isPulsing ? "scale-110" : "scale-100"}`} />
          <span className="hidden sm:inline font-mono">{isPulsing ? "♪" : "♫"}</span>
          <span className="hidden md:inline text-[10px] ml-1 opacity-70">HC</span>
        </>
      ) : (
        <>
          <span className="text-base leading-none">🎧</span>
          <span className="hidden sm:inline">Play Vibe</span>
        </>
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-sm">
          <span className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
        </span>
      )}
    </button>
  );
};

export default AudioController;
